import React, { useState, useEffect, useMemo } from 'react';
import api from '../api/api';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Button, Badge, Card, Input, Icon } from '../components/ui';
import { IconPercent, IconPrinter, IconFilter, IconX, IconInfoCircle } from '@tabler/icons-react';

export default function RSPage() {
  const { activeWorkspace } = useWorkspace();
  const [rsAccount, setRsAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', client: '', amountMin: '', amountMax: '' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { if (activeWorkspace) loadRSAccount(); }, [activeWorkspace]);

  const loadRSAccount = async () => {
    try {
      setLoading(true);
      const res = await api.get('/treasury/cash');
      const rs = (res.data.registers || []).find(r => r.name === 'Retenue à la Source');
      setRsAccount(rs || null);
      if (rs) { const res2 = await api.get(`/treasury/cash/${rs._id}/transactions`); setTransactions(res2.data.transactions || []); }
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const resetFilters = () => setFilters({ dateFrom: '', dateTo: '', client: '', amountMin: '', amountMax: '' });

  const filteredTransactions = useMemo(() => transactions.filter(tx => {
    if (filters.dateFrom) { const f = new Date(filters.dateFrom); f.setHours(0,0,0,0); if (new Date(tx.date) < f) return false; }
    if (filters.dateTo) { const t = new Date(filters.dateTo); t.setHours(23,59,59,999); if (new Date(tx.date) > t) return false; }
    if (filters.client.trim() && !(tx.description||'').toLowerCase().includes(filters.client.trim().toLowerCase())) return false;
    if (filters.amountMin !== '' && tx.amount < parseFloat(filters.amountMin)) return false;
    if (filters.amountMax !== '' && tx.amount > parseFloat(filters.amountMax)) return false;
    return true;
  }), [transactions, filters]);

  const activeFiltersCount = [filters.dateFrom, filters.dateTo, filters.client.trim(), filters.amountMin, filters.amountMax].filter(Boolean).length;
  const totalRS = useMemo(() => filteredTransactions.reduce((s, t) => s + t.amount, 0), [filteredTransactions]);
  const formatCurrency = (a) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(a);

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>État RS</title><style>body{font-family:Arial,sans-serif;margin:30px}h1{color:#b45309;border-bottom:2px solid #b45309;padding-bottom:10px}.solde{background:#fef3c7;padding:15px;border-radius:8px;margin:20px 0;font-size:18px;font-weight:bold;color:#92400e}table{width:100%;border-collapse:collapse;margin-top:20px}th{background:#fef3c7;color:#92400e;padding:10px;text-align:left;border:1px solid #fde68a}td{padding:8px 10px;border:1px solid #e5e7eb;font-size:13px}tr:nth-child(even){background:#fffbeb}.total{font-weight:bold;text-align:right;padding:15px;background:#fef3c7;font-size:16px}</style></head><body>
      <h1>ÉTAT DES RETENUES À LA SOURCE</h1>
      <div class="solde">Solde RS cumulé : ${rsAccount ? formatCurrency(rsAccount.currentBalance) : '—'}</div>
      <p><strong>Généré le :</strong> ${new Date().toLocaleDateString('fr-FR')}</p>
      <table><thead><tr><th>Date</th><th>Client</th><th>N° Certificat RS</th><th>N° Reçu</th><th style="text-align:right">Montant (DT)</th></tr></thead><tbody>
      ${filteredTransactions.map(tx => {
        const client = (tx.description||'').replace('Retenue à la source - ','').replace('Retenue à la source (migration) - ','');
        const ref = tx.reference || '';
        const isReceipt = ref.startsWith('PAY-');
        return `<tr><td>${new Date(tx.date).toLocaleDateString('fr-FR')}</td><td>${client}</td><td>${!isReceipt && ref ? ref : '—'}</td><td>${isReceipt ? ref : '—'}</td><td style="text-align:right;color:#059669;font-weight:bold">+${tx.amount.toFixed(3)}</td></tr>`;
      }).join('')}
      </tbody></table>
      <div class="total">Total : ${formatCurrency(totalRS)}</div>
      </body></html>`);
    w.document.close(); w.print();
  };

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-xl text-gray-600">Chargement...</div></div>;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800"><Icon name="IconPercent" className="text-yellow-600 mr-3" size={28} />Retenues à la Source</h1>
          <p className="text-gray-500 mt-1">Suivi des retenues à la source clients (RS)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={handlePrint}><Icon name="IconPrinter" size={16} className="mr-1" /> Imprimer</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="border-l-4 border-yellow-500 p-6">
          <div className="text-sm text-gray-500 mb-1">Solde RS cumulé</div>
          <div className="text-2xl font-bold text-yellow-700">{rsAccount ? formatCurrency(rsAccount.currentBalance) : '—'}</div>
          <div className="text-xs text-gray-400 mt-1">Créances fiscales à récupérer</div>
        </Card>
        <Card className="border-l-4 border-blue-500 p-6">
          <div className="text-sm text-gray-500 mb-1">Total RS (période filtrée)</div>
          <div className="text-2xl font-bold text-blue-700">{formatCurrency(totalRS)}</div>
          <div className="text-xs text-gray-400 mt-1">{filteredTransactions.length} transaction(s)</div>
        </Card>
        <Card className="border-l-4 border-purple-500 p-6">
          <div className="text-sm text-gray-500 mb-1">Total transactions</div>
          <div className="text-2xl font-bold text-purple-700">{transactions.length}</div>
          <div className="text-xs text-gray-400 mt-1">Depuis le début</div>
        </Card>
      </div>

      {!rsAccount && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Icon name="IconInfoCircle" className="text-yellow-600 shrink-0" size={24} />
          <div>
            <div className="font-semibold text-yellow-800">Aucun compte RS trouvé</div>
            <div className="text-sm text-yellow-700">Le compte sera créé automatiquement lors du premier règlement RS.</div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800">Historique des retenues</h2>
          <Button variant={showFilters || activeFiltersCount > 0 ? 'warning' : 'secondary'} size="sm" onClick={() => setShowFilters(!showFilters)}>
            <Icon name="IconFilter" size={16} className="mr-1" /> Filtres
            {activeFiltersCount > 0 && <Badge variant="warning">{activeFiltersCount}</Badge>}
          </Button>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Date début</label><Input type="date" value={filters.dateFrom} onChange={e => setFilters({...filters, dateFrom: e.target.value})} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label><Input type="date" value={filters.dateTo} onChange={e => setFilters({...filters, dateTo: e.target.value})} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Client / Description</label><Input type="text" placeholder="Rechercher..." value={filters.client} onChange={e => setFilters({...filters, client: e.target.value})} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Montant min</label><Input type="number" step="0.001" placeholder="0" value={filters.amountMin} onChange={e => setFilters({...filters, amountMin: e.target.value})} /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Montant max</label><Input type="number" step="0.001" placeholder="∞" value={filters.amountMax} onChange={e => setFilters({...filters, amountMax: e.target.value})} /></div>
              <div className="flex items-end"><Button variant="ghost" size="sm" onClick={resetFilters}><Icon name="IconX" size={16} className="mr-1" /> Réinitialiser</Button></div>
            </div>
            {activeFiltersCount > 0 && <div className="mt-3 pt-3 border-t border-yellow-200 text-xs text-yellow-700 font-medium">{filteredTransactions.length} résultat(s) sur {transactions.length} — Total filtré : {formatCurrency(totalRS)}</div>}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-yellow-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-yellow-800 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-yellow-800 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-yellow-800 uppercase">N° Certificat RS</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-yellow-800 uppercase">N° Reçu</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-yellow-800 uppercase">Montant RS (DT)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredTransactions.length === 0 ? (
                <tr><td colSpan="5" className="px-4 py-12 text-center text-gray-400"><Icon name="IconPercent" size={48} className="mb-3 block mx-auto opacity-30" />{activeFiltersCount > 0 ? 'Aucune retenue ne correspond aux filtres' : 'Aucune retenue à la source enregistrée'}</td></tr>
              ) : filteredTransactions.map(tx => {
                const client = (tx.description||'').replace('Retenue à la source - ','').replace('Retenue à la source (migration) - ','');
                const ref = tx.reference || '';
                const isReceipt = ref.startsWith('PAY-');
                return (
                  <tr key={tx._id} className="hover:bg-yellow-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-600">{new Date(tx.date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{client}</td>
                    <td className="px-4 py-3 text-sm">{!isReceipt && ref ? <Badge variant="warning">{ref}</Badge> : <span className="text-gray-400 text-xs">—</span>}</td>
                    <td className="px-4 py-3 text-sm">{isReceipt ? <Badge variant="info">{ref}</Badge> : <span className="text-gray-400 text-xs">—</span>}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-yellow-700">+{tx.amount.toFixed(3)}</td>
                  </tr>
                );
              })}
            </tbody>
            {filteredTransactions.length > 0 && (
              <tfoot className="bg-yellow-50 border-t-2 border-yellow-200">
                <tr>
                  <td colSpan="4" className="px-4 py-3 text-sm font-semibold text-yellow-800">Total ({filteredTransactions.length} retenue{filteredTransactions.length > 1 ? 's' : ''})</td>
                  <td className="px-4 py-3 text-right font-bold text-yellow-700 text-base">+{formatCurrency(totalRS)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
